import React from 'react';
import { getScoreColor } from '../insightsTheme';
import RankedBars from './RankedBars';

/**
 * Displays average scores for numeric questions using <RankedBars>.
 * Scores are normalized to a 0–100 bar width and shown on the original scale.
 *
 * Use <YesRateBars> for boolean questions.
 */

export interface AverageScoreRow {
    /** Project name or question topic shown beside the bar. */
    label: string;
    average: number;
    responses: number;
    questiontext?: string | null;
}

interface AverageScoreBarsProps {
    rows: AverageScoreRow[];
    /** Maximum score used to normalize bar width. */
    max: number;
    /** Optional fixed bar color. Otherwise, color is based on the score. */
    barColor?: string;
    /** Optional fixed color for the value text. Otherwise, it follows the bar color. */
    valueColor?: string;
    /** Maximum number of highest-scoring rows to display. */
    limit?: number;
}

const AverageScoreBars: React.FC<AverageScoreBarsProps> = ({ rows, max, barColor, valueColor, limit }) => (
    <RankedBars
        rows={rows}
        limit={limit}
        valueColor={valueColor}
        rank={(row) => row.average}
        toBar={(row) => {
            const pct = Math.min(100, Math.max(0, max > 0 ? (row.average / max) * 100 : 0));

            return {
                label: row.label,
                questiontext: row.questiontext,
                percentage: pct,
                displayValue: `${row.average.toFixed(1)} / ${max}`,
                tooltip: `Average of ${row.responses.toLocaleString()} ${row.responses === 1 ? 'response' : 'responses'}`,

                // Set per row, so each bar can have its own colour based on its value.
                barColor: barColor ?? getScoreColor(pct),
            };
        }}
    />
);

export default AverageScoreBars;
